<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { reactive, onMounted, computed, ref, onUnmounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import ProductCarousel from '@/Components/Carousels/ProductCarousel.vue';

const page = usePage();

const user = computed(
    () => page.props.auth.user
);

//get products from page props
const popular_products = computed(() => page.props.popular_products);


// Vis et forbogstav-fallback hvis logo-filen mangler/ikke kan loades (404),
// så vi aldrig render et "broken image"-ikon.

const popularCategories = computed(() => page.props.popular_categories);

const prisfald = computed(() => page.props.prisfald ?? []);
const kategoriUdvikling = computed(() => page.props.kategoriUdvikling ?? []);
const mestSete = computed(() => page.props.mestSete ?? []);
const stoersteFald = computed(() => page.props.stoersteFald ?? []);
const daekning = computed(() => page.props.daekning ?? null);

/*
 * Et doedt billede maa ikke bryde raekken.
 *
 * Kataloget rummer varer hvis billedfil er vaek, og artikler fra foer
 * cover_image blev obligatorisk. Uden det her ville de vise browserens
 * broken-image-ikon midt i en taet liste. Vi bytter til den tomme plads i
 * stedet, saa kolonnerne stadig flugter.
 */
function skjulBillede(e) {
    const img = e.target;
    const plads = document.createElement('span');
    plads.className = 'fp-liste__miniatur--tom';
    img.replaceWith(plads);
}

/** Tal med dansk tusindtalsseparator. */
function tal(n) {
    return new Intl.NumberFormat('da-DK').format(Math.round(Number(n) || 0));
}

const MAANEDER = ['januar','februar','marts','april','maj','juni','juli','august','september','oktober','november','december'];

/** "30. aug" — kort nok til at staa i en taet liste. */
function kortDag(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()}. ${MAANEDER[d.getMonth()].slice(0, 3)}`;
}

function kortDato(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${MAANEDER[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * "i dag" siger mere end en dato paa et daekningstal.
 * Er stemplet aeldre end et doegn, staar datoen der i stedet, saa det
 * aldrig kommer til at se friskere ud end det er.
 */
function relativDato(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const timer = (Date.now() - d.getTime()) / 36e5;
    if (timer < 24) return 'i dag';
    if (timer < 48) return 'i går';
    return `${d.getDate()}. ${MAANEDER[d.getMonth()]}`;
}

const latestArticles = computed(() => Array.isArray(page.props.latest_article) ? page.props.latest_article : []);

/** Faerdige maskiner fra PC-byggeren. Se PageController::forsidensBuilds. */
const offentligeBuilds = computed(() => Array.isArray(page.props.offentligeBuilds) ? page.props.offentligeBuilds : []);

/** Hele kroner. Forsiden viser ikke oerer; de stoejer mere end de oplyser. */
function kroner(tal) {
    const n = Number(tal);
    if (! Number.isFinite(n) || n <= 0) return '';
    return `${n.toLocaleString('da-DK', { maximumFractionDigits: 0 })} kr.`;
}

const selectedArticleIndex = ref(0);
let autoCycleTimer = null;
let isPaused = false;

function selectArticle(idx) {
    selectedArticleIndex.value = idx;
}

function pauseAutoCycle() {
    isPaused = true;
    if (autoCycleTimer) clearInterval(autoCycleTimer);
}

function resumeAutoCycle() {
    isPaused = false;
    if (latestArticles.value.length > 1 && !autoCycleTimer) {
        autoCycleTimer = setInterval(() => {
            if (!isPaused) {
                selectedArticleIndex.value = (selectedArticleIndex.value + 1) % latestArticles.value.length;
            }
        }, 5000);
    }
}

onMounted(() => {
    if (latestArticles.value.length > 1) {
        autoCycleTimer = setInterval(() => {
            if (!isPaused) {
                selectedArticleIndex.value = (selectedArticleIndex.value + 1) % latestArticles.value.length;
            }
        }, 5000);
    }
});

onUnmounted(() => {
    if (autoCycleTimer) clearInterval(autoCycleTimer);
});

/*
 * Ikon efter hvad kategorien ER, ikke efter hvor den staar i listen.
 *
 * Ikonerne blev tildelt efter raekkefoelge. Det gik saa laenge listen var
 * fast, men kategorierne rangeres nu efter maalte besoeg, og saa fik
 * Hovedtelefoner et RAM-chip-ikon og Switch et tastatur. Et ikon der ikke
 * passer er vaerre end ingen, for det faar folk til at tvivle paa resten.
 */
const IKONER = [
    [/ram|hukommelse/i, 'bi-memory'],
    [/grafik|gpu/i, 'bi-gpu-card'],
    [/processor|cpu/i, 'bi-cpu'],
    [/bundkort/i, 'bi-motherboard'],
    [/skærm|skaerm|monitor|tv/i, 'bi-display'],
    [/keyboard|tastatur/i, 'bi-keyboard'],
    [/mus|mouse/i, 'bi-mouse'],
    [/hovedtelefon|headset|høretelefon|ørebøffer/i, 'bi-headphones'],
    [/højtaler|hojtaler|lyd|soundbar/i, 'bi-speaker'],
    [/mobil|telefon|smartphone/i, 'bi-phone'],
    [/tablet|ipad/i, 'bi-tablet'],
    [/switch|router|netværk|netvaerk|wifi|adgangspunkt/i, 'bi-hdd-network'],
    [/ssd|harddisk|nvme|lagring|disk/i, 'bi-device-hdd'],
    [/kamera|foto|objektiv/i, 'bi-camera'],
    [/printer|blæk|blaek|toner/i, 'bi-printer'],
    [/belysning|lampe|pære|paere|smart.?hjem/i, 'bi-lightbulb'],
    [/kabel|adapter/i, 'bi-usb-symbol'],
    [/køl|koel|blæser|blaeser|ventilator/i, 'bi-fan'],
    [/strømforsyning|stroemforsyning|psu/i, 'bi-plug'],
    [/kabinet|chassis/i, 'bi-pc'],
    [/bærbar|baerbar|laptop/i, 'bi-laptop'],
    [/konsol|playstation|xbox|spil/i, 'bi-controller'],
    [/ur|watch/i, 'bi-smartwatch'],
    [/cover|etui|taske/i, 'bi-bag'],
];

function kategoriIkon(navn) {
    for (const [moenster, ikon] of IKONER) {
        if (moenster.test(navn || '')) return ikon;
    }

    return 'bi-box';
}

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
});
</script>

<template>
    <PriceCompareLayout>
        <div class="container-site fp-page">

            <!-- Hero Section -->
            <!--
                Taet forside.
                Foerste forsoeg havde et soegefelt i heroen, men headeren har
                allerede eet, og mit var det tredje input[name=q] paa siden.
                Blokkene laa desuden stablet i fuld bredde: otte prisraekker
                fyldte 640px paa desktop, altsaa 80px pr. raekke, og kun to
                blokke naaede over folden. Det er praecis den stoerrelse
                laeserne forlod PriceRunner over.
            -->
            <section class="fp-top">
                <h1 class="fp-top__title">Sammenlign priser på hardware og elektronik</h1>
                <p v-if="daekning" class="fp-top__tal">
                    <span><strong>{{ tal(daekning.varer) }}</strong> varer</span>
                    <span><strong>{{ tal(daekning.butikker) }}</strong> butikker</span>
                    <span><strong>{{ tal(daekning.maalinger) }}</strong> prismålinger</span>
                    <span v-if="daekning.opdateret">opdateret {{ relativDato(daekning.opdateret) }}</span>
                </p>
            </section>

            <!--
                PRODUKTERNE OEVERST, og det er maalt.

                MAALT 16. oktober 2026 paa 1920px: de foerste 914px af
                forsiden indeholdt ikke ét billede. Seks tabeller i to raekker
                fyldte 712px over folden, og det stoerste billede paa HELE
                siden var 182px — mod produktsidens hovedbillede paa 336.
                Varerne laa under folden.

                Tabellerne er stadig sidens styrke; de er bare ikke det
                foerste man skal moede. En vare med et billede og en pris
                siger paa ét sekund hvad siden er til.
            -->
            <section v-if="popular_products && popular_products.length" class="fp-section fp-section--foerst">
                <h2 class="fp-section__title">Populære produkter</h2>
                <ProductCarousel componentIdentifier="a1" :products="popular_products" :headline="''" :productsPrPage="5" />
            </section>

            <!--
                Foerste raekke er nu BRED og SMAL, ikke tre ens. "Laveste pris
                vi har maalt" er den eneste af blokkene der er vores egen
                maaling og ikke en liste, saa den faar to tredjedele.
            -->
            <div class="fp-bento">
                <section v-if="prisfald.length" class="fp-kol fp-bento__bred">
                    <h2 class="fp-kol__titel">Laveste pris vi har målt</h2>
                    <ul class="fp-liste">
                        <li v-for="v in prisfald" :key="v.slug" class="fp-liste__linje">
                            <Link :href="`/produkt/${v.slug}`" class="fp-liste__miniatur" tabindex="-1" aria-hidden="true">
                                <img
                                    v-if="v.billede"
                                    :src="`/images/${v.billede}?w=96&fmt=webp`"
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    width="28"
                                    height="28"
                                    @error="skjulBillede"
                                >
                                <span v-else class="fp-liste__miniatur--tom"></span>
                            </Link>
                            <Link :href="`/produkt/${v.slug}`" class="fp-liste__navn">{{ v.navn }}</Link>
                            <span class="fp-liste__nr">{{ v.butikker }}</span>
                            <span class="fp-liste__tal">{{ tal(v.pris) }} kr.</span>
                        </li>
                    </ul>
                    <p class="fp-kol__note">Vores egen måling, ikke butikkens oplyste førpris. Tallet er antal butikker.</p>
                </section>

                <section class="fp-kol">
                    <h2 class="fp-kol__titel">Populære kategorier</h2>
                    <ul class="fp-liste">
                        <li v-for="k in popularCategories" :key="k.id" class="fp-liste__linje">
                            <Link
                                :href="route('subSubCategory', {
                                    categorySlug: k.main_category_slug,
                                    subCategorySlug: k.sub_category_slug,
                                    subSubCategorySlug: k.slug
                                })"
                                class="fp-liste__navn"
                            >{{ k.name }}</Link>
                            <span class="fp-liste__tal">{{ tal(k.antal) }} varer</span>
                        </li>
                    </ul>
                </section>

            </div>

            <!--
                BRED OG SMAL PAA SAMME LINJE.
                
                Siden var fire baand af ens kasser: tre kolonner, tre kolonner,
                en karrusel, en tekst. Alt lige bredt, alt lige vigtigt, og
                derfor ingen rangordning. Her faar maskinerne to tredjedele og
                artiklerne én — og artiklerne faar deres forsidebillede i
                stedet for en 28px miniature.
            -->
            <div v-if="offentligeBuilds.length || latestArticles.length" class="fp-bento">
                <section v-if="offentligeBuilds.length" class="fp-kol fp-bento__bred">
                    <h2 class="fp-kol__titel">Færdige maskiner du kan bygge</h2>
                    <div class="fp-builds">
                        <Link v-for="b in offentligeBuilds" :key="b.slug"
                              :href="`/pc-bygger/b/${b.url_slug || b.slug}`" class="fp-build">
                            <span class="fp-build__billeder">
                                <img v-for="d in b.dele.filter(x => x.billede_id).slice(0, 4)" :key="d.slug"
                                     :src="`/images/${d.billede_id}?w=96&fmt=webp`"
                                     :srcset="`/images/${d.billede_id}?w=96&fmt=webp 1x, /images/${d.billede_id}?w=160&fmt=webp 2x`"
                                     :alt="d.navn" width="40" height="40" loading="lazy" decoding="async" />
                            </span>
                            <span class="fp-build__navn">{{ b.navn }}</span>
                            <span class="fp-build__pris">{{ kroner(b.pris) }}</span>
                            <span class="fp-build__meta">{{ b.antal_dele }} dele<template v-if="b.estimeret_watt"> · ~{{ b.estimeret_watt }} W</template></span>
                        </Link>
                    </div>
                    <Link :href="route('pcbygger.index')" class="fp-kol__mere">Se PC-byggeren</Link>
                </section>

                <section v-if="latestArticles.length" class="fp-kol">
                    <h2 class="fp-kol__titel">Nyt fra artiklerne</h2>
                    <Link v-if="latestArticles[0]" :href="`/artikler/${latestArticles[0].name_url_friendly}`" class="fp-artikel">
                        <img v-if="latestArticles[0].cover_image"
                             :src="route('getArticleImage', { path: latestArticles[0].cover_image, w: 320, fmt: 'webp' })"
                             alt="" class="fp-artikel__billede" width="320" height="160"
                             loading="lazy" decoding="async" @error="skjulBillede" />
                        <span class="fp-artikel__navn">{{ latestArticles[0].name }}</span>
                        <span v-if="latestArticles[0].teaser_text" class="fp-artikel__teaser">{{ latestArticles[0].teaser_text }}</span>
                    </Link>
                    <!--
                        MINIATUREN BLIVER. Martin 12/9: "den taette forside er
                        rigtig, men ren tekst er kedelig. Begge lister skal have
                        en lille miniature — og en tom plads naar billedet
                        mangler, ellers flugter navnene ikke."

                        Jeg fjernede den da artiklen oeverst fik sit store
                        billede, og proeven fangede det. Den tomme plads er
                        lige saa vigtig som billedet: uden den springer navnene
                        i listen.
                    -->
                    <ul class="fp-liste">
                        <li v-for="a in latestArticles.slice(1, 4)" :key="a.id" class="fp-liste__linje fp-liste__linje--artikel">
                            <Link :href="`/artikler/${a.name_url_friendly}`" class="fp-liste__miniatur" tabindex="-1" aria-hidden="true">
                                <img
                                    v-if="a.cover_image"
                                    :src="route('getArticleImage', { path: a.cover_image, w: 96, fmt: 'webp' })"
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                    width="28"
                                    height="28"
                                    @error="skjulBillede"
                                >
                                <span v-else class="fp-liste__miniatur--tom"></span>
                            </Link>
                            <Link :href="`/artikler/${a.name_url_friendly}`" class="fp-liste__navn">{{ a.name }}</Link>
                            <span class="fp-liste__tal">{{ kortDag(a.post_date) }}</span>
                        </li>
                    </ul>
                    <Link :href="route('articles')" class="fp-kol__mere">Alle artikler</Link>
                </section>
            </div>

            <!--
                Anden raekke. Alt herunder er forudberegnet i forside:opdater-tal
                og laeses faerdigt; forsiden taeller aldrig selv.
            -->
            <div v-if="stoersteFald.length || mestSete.length || kategoriUdvikling.length" class="fp-kolonner">
                <section v-if="stoersteFald.length" class="fp-kol">
                    <h2 class="fp-kol__titel">Største prisfald i går</h2>
                    <ul class="fp-liste">
                        <li v-for="v in stoersteFald" :key="v.slug" class="fp-liste__linje">
                            <Link :href="`/produkt/${v.slug}`" class="fp-liste__navn">{{ v.navn }}</Link>
                            <span class="fp-liste__foer">{{ tal(v.foer) }}</span>
                            <span class="fp-liste__tal">{{ tal(v.nu) }} kr.</span>
                            <span class="fp-liste__fald">−{{ v.pct }}%</span>
                        </li>
                    </ul>
                    <p class="fp-kol__note">Målt på laveste pris inkl. fragt. Fald over 70% er udeladt, de er næsten altid målefejl.</p>
                </section>

                <section v-if="mestSete.length" class="fp-kol">
                    <h2 class="fp-kol__titel">Mest sete denne uge</h2>
                    <ul class="fp-liste">
                        <li v-for="v in mestSete" :key="v.slug" class="fp-liste__linje">
                            <Link :href="`/produkt/${v.slug}`" class="fp-liste__navn">{{ v.navn }}</Link>
                            <span class="fp-liste__nr">{{ tal(v.seere) }}</span>
                            <span class="fp-liste__tal">{{ tal(v.pris) }} kr.</span>
                        </li>
                    </ul>
                    <p class="fp-kol__note">Tallet er unikke besøgende, ikke sidevisninger.</p>
                </section>

                <section v-if="kategoriUdvikling.length" class="fp-kol">
                    <h2 class="fp-kol__titel">Her falder priserne</h2>
                    <ul class="fp-liste">
                        <!--
                            k.url mangler i tal der er gemt foer rettelsen.
                            Et link uden href er ikke bedre end et doedt et.
                        -->
                        <li v-for="k in kategoriUdvikling.filter((x) => x.url)" :key="k.slug" class="fp-liste__linje">
                            <!--
                                Hele stien, ikke kategoriens eget slug.
                                /kategori/{slug} er ikke en rute — seks links
                                paa forsiden svarede 404.
                            -->
                            <Link :href="k.url" class="fp-liste__navn">{{ k.navn }}</Link>
                            <span class="fp-liste__nr">{{ tal(k.varer) }}</span>
                            <span class="fp-liste__fald">{{ k.andel }}% faldet</span>
                        </li>
                    </ul>
                    <p class="fp-kol__note">Andel af kategoriens varer der faldt i pris i går. Tallet er antal varer.</p>
                </section>
            </div>

            <!-- Popular Products -->
            <!--
                Staar nederst med vilje. Den er til soegemaskiner og
                sprogmodeller, ikke til den der allerede er paa siden, og den
                maa ikke tage plads fra data.
            -->
            <section class="fp-om">
                <h2 class="fp-om__titel">Hvad du kan bruge siden til</h2>
                <p>
                    PriceCompare sammenligner priser på hardware og elektronik hos danske forhandlere.
                    Vi følger <strong v-if="daekning">{{ tal(daekning.varer) }}</strong> varer hos
                    <strong v-if="daekning">{{ tal(daekning.butikker) }}</strong> butikker og gemmer hver
                    prisændring, så du kan se hvad en vare plejer at koste i stedet for kun hvad den koster i dag.
                    Prishistorik på hver vare, filtre på de tekniske specifikationer, prisalarm når prisen falder,
                    og alle de butikker vi kender, også dem vi ikke tjener på.
                </p>
            </section>

        </div>
    </PriceCompareLayout>
</template>

<style scoped>
/* ── Taet forside ──
   Maalt paa den foerste udgave: heroen 305px, prislisten 640px for otte
   raekker, og kun to blokke over folden. Alt herunder handler om at faa
   flere elementer paa samme linje og mere data pr. pixel. */

.fp-page {
    padding-top: 1rem;
    padding-bottom: 2rem;
}

/* Overskrift og daekningstal paa samme linje. */
.fp-top {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.35rem 1.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--pc-border);
}

.fp-top__title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 700;
}

.fp-top__tal {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    margin: 0;
    font-size: var(--pc-text-sm);
    color: var(--pc-text-muted);
}

/* Tre kolonner ved siden af hinanden i stedet for tre blokke stablet. */
/*
 * BENTO: bred og smal paa samme linje.
 *
 * Siden var fire baand af ens kasser — tre kolonner, tre kolonner, en
 * karrusel, en tekst. Alt lige bredt betyder at alt er lige vigtigt, og saa
 * er der ingen rangordning. To tredjedele mod én giver et oeje et sted at
 * begynde, og den smalle kolonne er stadig bred nok til en liste.
 */
.fp-bento {
    display: grid;
    min-width: 0;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 1.25rem;
    margin-bottom: 1.5rem;
}

.fp-bento__bred {
    min-width: 0;
}

/* ── Faerdige maskiner ── */

.fp-builds {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 0.75rem;
}

.fp-build {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.7rem;
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius);
    background: var(--pc-bg-tertiary);
    text-decoration: none;
    transition: border-color var(--pc-transition);
}

.fp-build:hover {
    border-color: var(--pc-accent);
}

.fp-build__billeder {
    display: flex;
    gap: 0.25rem;
    margin-bottom: 0.3rem;
}

.fp-build__billeder img {
    width: 40px;
    height: 40px;
    object-fit: contain;
    background: #fff;
    border-radius: 0.25rem;
    padding: 2px;
}

.fp-build__navn {
    color: var(--pc-text-primary);
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 1.3;
}

.fp-build__pris {
    color: var(--pc-accent);
    font-weight: 700;
}

.fp-build__meta {
    color: var(--pc-text-muted);
    font-size: 0.78rem;
}

/* ── Artiklen med sit eget billede ── */

.fp-artikel {
    display: block;
    text-decoration: none;
    margin-bottom: 0.75rem;
}

.fp-artikel__billede {
    width: 100%;
    height: auto;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    border-radius: var(--pc-radius);
    margin-bottom: 0.45rem;
    background: var(--pc-bg-tertiary);
}

.fp-artikel__navn {
    display: block;
    color: var(--pc-text-primary);
    font-weight: 600;
    line-height: 1.35;
}

.fp-artikel__teaser {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: var(--pc-text-secondary);
    font-size: 0.85rem;
    margin-top: 0.2rem;
}

.fp-kolonner {
    display: grid;
    min-width: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
    margin-bottom: 1.5rem;
}

/* Et gitterbarn har min-width: auto og kan derfor blive bredere end sin
   kolonne. Maalt paa 390px: 7px vandret overloeb. */
.fp-kol {
    min-width: 0;
}

.fp-kol__titel {
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.01em;
}

.fp-kol__note {
    margin: 0.4rem 0 0;
    font-size: 0.75rem;
    color: var(--pc-text-muted);
}

.fp-kol__mere {
    display: inline-block;
    margin-top: 0.4rem;
    font-size: var(--pc-text-sm);
    font-weight: 600;
    color: var(--pc-accent);
    text-decoration: none;
}

/* Een linje pr. raekke. Navn, tal, tal. */
.fp-liste {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid var(--pc-border);
    border-radius: var(--pc-radius);
    overflow: hidden;
}

/*
 * Miniature i den taette liste.
 *
 * Martin 12/9: ren tekst i kompakt format er korrekt men kedeligt. 28px giver
 * oejet noget at hvile paa uden at raekkehoejden vokser — teksten er 1.35 em,
 * altsaa cirka 19px, saa billedet saetter hoejden men kun med nogle faa px.
 */
.fp-liste__miniatur {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    overflow: hidden;
    background: var(--pc-bg-secondary);
}

.fp-liste__miniatur img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/* Samme plads som et billede, saa navnene flugter uanset om der er et. */
.fp-liste__miniatur--tom {
    display: block;
    width: 28px;
    height: 28px;
    border-radius: 3px;
    background: var(--pc-bg-secondary);
}

/* Foer-prisen: til stede, men maa ikke konkurrere med den aktuelle. */
.fp-liste__foer {
    flex: 0 0 auto;
    color: var(--pc-text-muted);
    text-decoration: line-through;
    font-variant-numeric: tabular-nums;
}

.fp-liste__fald {
    flex: 0 0 auto;
    color: var(--pc-success, #1a7f37);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.fp-liste__linje {
    display: flex;
    /* center frem for baseline: ellers haenger miniaturen under teksten. */
    align-items: center;
    gap: 0.6rem;
    padding: 0.32rem 0.6rem;
    font-size: var(--pc-text-sm);
    line-height: 1.35;
    border-bottom: 1px solid var(--pc-border);
}

.fp-liste__linje:last-child { border-bottom: 0; }
.fp-liste__linje:nth-child(odd) { background: var(--pc-bg-secondary); }

.fp-liste__navn {
    flex: 1;
    min-width: 0;
    color: var(--pc-text-primary);
    text-decoration: none;
    /* Een linje. Et navn der wrapper goer raekken dobbelt saa hoej. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fp-liste__navn:hover { color: var(--pc-accent); }

/* Artikeltitler er lange, saa de faar to linjer i stedet for at blive klippet. */
.fp-liste__linje--artikel .fp-liste__navn {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.fp-liste__nr {
    flex: 0 0 auto;
    min-width: 1.4rem;
    text-align: right;
    font-size: 0.75rem;
    color: var(--pc-text-muted);
}

.fp-liste__tal {
    flex: 0 0 auto;
    font-weight: 600;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

/* ── Om-afsnittet, nederst ── */
.fp-om {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--pc-border);
}

.fp-om__titel {
    margin: 0 0 0.4rem;
    font-size: 0.95rem;
    font-weight: 700;
}

.fp-om p {
    margin: 0;
    max-width: 80ch;
    font-size: var(--pc-text-sm);
    color: var(--pc-text-muted);
}

/* ── Populaere produkter ── */
.fp-section {
    margin-bottom: 1.5rem;
}

.fp-section__title {
    margin: 0 0 0.5rem;
    font-size: 0.95rem;
    font-weight: 700;
}

/* Under 1100px bliver tre kolonner for smalle til et varenavn. */
@media (max-width: 1100px) {
    .fp-kolonner { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .fp-bento { grid-template-columns: minmax(0, 1fr); }
}

@media (max-width: 700px) {
    .fp-kolonner { grid-template-columns: 1fr; gap: 1rem; }
    .fp-top { flex-direction: column; align-items: flex-start; }
    .fp-top__title { font-size: 1.15rem; }
}
</style>
